import { useState, useEffect, useRef } from 'react';
import { X, Play, Pause, RotateCcw, Volume2, VolumeX, Home } from 'lucide-react';
import { playMetronomeTick, playBowlChime, speakInstruction } from '@/utils/audioUtils';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR } from '@/utils/exerciseBackgrounds';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseTopLogo from './ExerciseTopLogo';

const RussianMastersModal = ({ isOpen, onClose, onSessionComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [currentRatioIndex, setCurrentRatioIndex] = useState(0);
  const [breathInCycle, setBreathInCycle] = useState(0);
  const [totalBreaths, setTotalBreaths] = useState(0);
  const [startTime, setStartTime] = useState(null);
  
  const containerRef = useRef(null);
  const phaseTimerRef = useRef(null);
  const metronomeRef = useRef(null);
  const soundEnabledRef = useRef(true);
  const breathInCycleRef = useRef(0);
  const currentRatioIndexRef = useRef(0);
  const isActiveRef = useRef(false);
  const isPausedRef = useRef(false);

  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { breathInCycleRef.current = breathInCycle; }, [breathInCycle]);
  useEffect(() => { currentRatioIndexRef.current = currentRatioIndex; }, [currentRatioIndex]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  const ratioSequence = [
    { inhale: 5, exhale: 5, label: '5, 5' },
    { inhale: 6, exhale: 4, label: '6, 4' },
    { inhale: 7, exhale: 3, label: '7, 3' },
    { inhale: 8, exhale: 2, label: '8, 2' },
    { inhale: 9, exhale: 1, label: '9, 1' },
    { inhale: 8, exhale: 2, label: '8, 2' },
    { inhale: 7, exhale: 3, label: '7, 3' },
    { inhale: 6, exhale: 4, label: '6, 4' },
    { inhale: 5, exhale: 5, label: '5, 5' },
    { inhale: 4, exhale: 6, label: '4, 6' },
    { inhale: 3, exhale: 7, label: '3, 7' },
    { inhale: 2, exhale: 8, label: '2, 8' },
    { inhale: 1, exhale: 9, label: '1, 9' },
    { inhale: 2, exhale: 8, label: '2, 8' },
    { inhale: 3, exhale: 7, label: '3, 7' },
    { inhale: 4, exhale: 6, label: '4, 6' },
    { inhale: 5, exhale: 5, label: '5, 5' },
  ];

  const breathsPerRatio = 5;
  const totalRatios = ratioSequence.length;
  const totalExerciseBreaths = totalRatios * breathsPerRatio;
  const currentRatio = ratioSequence[currentRatioIndex] || ratioSequence[0];

  const playInhaleTone = () => {
    if (soundEnabledRef.current) playBowlChime('high');
  };

  const playExhaleTone = () => {
    if (soundEnabledRef.current) playBowlChime('low');
  };

  const animateBreathing = (expanding, duration) => {
    if (containerRef.current) {
      containerRef.current.style.transition = `transform ${duration}s ease-in-out`;
      containerRef.current.style.transform = expanding ? 'scale(1.4)' : 'scale(1)';
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsActive(false);
      setIsPaused(false);
      setPhase('ready');
      setCurrentRatioIndex(0);
      setBreathInCycle(0);
      setTotalBreaths(0);
      breathInCycleRef.current = 0;
      currentRatioIndexRef.current = 0;
      if (containerRef.current) {
        containerRef.current.style.transition = 'none';
        containerRef.current.style.transform = 'scale(1)';
      }
    }
    return () => {
      if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
      if (metronomeRef.current) clearInterval(metronomeRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (metronomeRef.current) {
      clearInterval(metronomeRef.current);
      metronomeRef.current = null;
    }
    if (!isActive || isPaused) return;
    if (soundEnabledRef.current) playMetronomeTick();
    metronomeRef.current = setInterval(() => {
      if (soundEnabledRef.current && isActiveRef.current && !isPausedRef.current) {
        playMetronomeTick();
      }
    }, 1000);
    return () => {
      if (metronomeRef.current) {
        clearInterval(metronomeRef.current);
        metronomeRef.current = null;
      }
    };
  }, [isActive, isPaused]);

  const runBreathCycle = () => {
    if (!isActiveRef.current || isPausedRef.current) return;

    const ratio = ratioSequence[currentRatioIndexRef.current];
    
    // INHALE
    setPhase('inhale');
    playInhaleTone();
    animateBreathing(true, ratio.inhale);
    
    phaseTimerRef.current = setTimeout(() => {
      if (!isActiveRef.current || isPausedRef.current) return;
      
      // EXHALE
      setPhase('exhale');
      playExhaleTone();
      animateBreathing(false, ratio.exhale);
      
      phaseTimerRef.current = setTimeout(() => {
        if (!isActiveRef.current || isPausedRef.current) return;
        
        setTotalBreaths(prev => prev + 1);
        
        const newBreathInCycle = (breathInCycleRef.current + 1) % breathsPerRatio;
        breathInCycleRef.current = newBreathInCycle;
        setBreathInCycle(newBreathInCycle);
        
        if (newBreathInCycle === 0) {
          const newRatioIndex = currentRatioIndexRef.current + 1;
          
          if (newRatioIndex >= totalRatios) {
            // Exercise completed - save session
            if (onSessionComplete && startTime) {
              const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
              onSessionComplete({
                patternId: 'russian-masters',
                patternName: 'Maîtres Russes',
                durationSeconds: elapsedSeconds,
                cyclesCompleted: totalBreaths + 1
              });
            }
            setIsActive(false);
            setPhase('complete');
            return;
          }
          
          currentRatioIndexRef.current = newRatioIndex;
          setCurrentRatioIndex(newRatioIndex);
          
          if (soundEnabledRef.current) {
            speakInstruction(ratioSequence[newRatioIndex].label, 'fr-FR');
          }
        }
        
        runBreathCycle();
      }, ratio.exhale * 1000);
    }, ratio.inhale * 1000);
  };

  const handleStart = () => {
    isActiveRef.current = true;
    isPausedRef.current = false;
    setIsActive(true);
    setIsPaused(false);
    setStartTime(Date.now());
    
    if (soundEnabledRef.current) {
      speakInstruction(ratioSequence[currentRatioIndexRef.current].label, 'fr-FR');
    }
    
    setTimeout(() => runBreathCycle(), 200);
  };

  const handlePause = () => {
    if (isPaused) {
      isPausedRef.current = false;
      setIsPaused(false);
      runBreathCycle();
    } else {
      isPausedRef.current = true;
      setIsPaused(true);
      if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
      if (containerRef.current) {
        const computed = window.getComputedStyle(containerRef.current);
        containerRef.current.style.transition = 'none';
        containerRef.current.style.transform = computed.transform;
      }
    }
  };

  const handleReset = () => {
    // Save session before reset if practiced for more than 10 seconds
    if (onSessionComplete && startTime) {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      if (elapsedSeconds > 10) {
        onSessionComplete({
          patternId: 'russian-masters',
          patternName: 'Maîtres Russes',
          durationSeconds: elapsedSeconds,
          cyclesCompleted: totalBreaths
        });
      }
    }
    
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    if (metronomeRef.current) clearInterval(metronomeRef.current);
    
    isActiveRef.current = false;
    isPausedRef.current = false;
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setCurrentRatioIndex(0);
    setBreathInCycle(0);
    setTotalBreaths(0);
    setStartTime(null);
    breathInCycleRef.current = 0;
    currentRatioIndexRef.current = 0;
    
    if (containerRef.current) {
      containerRef.current.style.transition = 'transform 0.3s ease';
      containerRef.current.style.transform = 'scale(1)';
    }
  };

  const getPhaseLabel = () => {
    if (phase === 'ready') return 'Prêt';
    if (phase === 'complete') return 'Terminé !';
    if (phase === 'inhale') return 'Inspirez';
    if (phase === 'exhale') return 'Expirez';
    return '';
  };

  const getPhaseColor = () => {
    if (phase === 'inhale') return '#22d3ee';
    if (phase === 'exhale') return '#a78bfa';
    if (phase === 'complete') return '#34d399';
    return '#94a3b8';
  };

  const overallProgress = Math.round((totalBreaths / totalExerciseBreaths) * 100);

  const getPhaseDescription = () => {
    if (currentRatioIndex < 5) return 'Phase 1 : Inspir croissant';
    if (currentRatioIndex < 9) return 'Phase 2 : Retour à 5/5';
    if (currentRatioIndex < 13) return 'Phase 3 : Expir croissant';
    return 'Phase 4 : Retour final';
  };

  const circumference = 2 * Math.PI * 85;
  const inhaleArc = (currentRatio.inhale / 10) * circumference;
  const exhaleArc = (currentRatio.exhale / 10) * circumference;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <ExerciseCopyright />
      <div 
        className="relative w-full h-full flex flex-col"
        style={{ 
          backgroundImage: `url(${EXERCISE_BACKGROUNDS.russianMasters})`,
          backgroundColor: EXERCISE_BG_FALLBACK_COLOR,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Floating Navigation Buttons */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={onClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Retour aux exercices"
          >
            <Home size={20} className="text-white" />
          </button>
        </div>
        
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={onClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Fermer"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        <ExerciseTopLogo />

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 pt-16 pb-32">
          
          {/* Ratio Display */}
          <div className="mb-6 text-center">
            <div className="bg-white/10 backdrop-blur rounded-2xl px-6 py-3 mb-2">
              <span className="text-4xl sm:text-6xl font-bold text-white">
                {currentRatio.inhale} : {currentRatio.exhale}
              </span>
            </div>
            <p className="text-white/60 text-sm">Respiration {breathInCycle + 1}/5 du cycle</p>
          </div>

          {/* Breathing Animation */}
          <div 
            ref={containerRef}
            className="relative mb-6 will-change-transform"
            style={{ width: '200px', height: '200px', transform: 'scale(1)' }}
          >
            {/* Outer Ring SVG */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
              <circle cx="100" cy="100" r="85" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
              <circle
                cx="100" cy="100" r="85"
                fill="none" stroke="#22d3ee" strokeWidth="6"
                strokeDasharray={`${inhaleArc} ${circumference}`}
                strokeLinecap="round"
                transform="rotate(-90 100 100)"
              />
              <circle
                cx="100" cy="100" r="85"
                fill="none" stroke="#a78bfa" strokeWidth="6"
                strokeDasharray={`${exhaleArc} ${circumference}`}
                strokeDashoffset={-inhaleArc}
                strokeLinecap="round"
                transform="rotate(-90 100 100)"
              />
            </svg>

            {/* Inner Circle */}
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{ padding: '25px' }}
            >
              <div
                className="w-full h-full rounded-full flex items-center justify-center shadow-2xl"
                style={{ background: `linear-gradient(135deg, ${getPhaseColor()}, ${getPhaseColor()}dd)` }}
              >
                <span className="text-white text-xl font-bold drop-shadow-lg">{getPhaseLabel()}</span>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="w-full max-w-sm mb-4">
            <div className="flex justify-between text-white/60 text-sm mb-1">
              <span>Progression</span>
              <span>{totalBreaths}/{totalExerciseBreaths} ({overallProgress}%)</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-400 to-violet-500" style={{ width: `${overallProgress}%` }} />
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-4 text-white/70 text-sm mb-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-cyan-400" />
              <span>Inspir ({currentRatio.inhale}s)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-violet-400" />
              <span>Expir ({currentRatio.exhale}s)</span>
            </div>
          </div>

          {/* Info Panel */}
          {(!isActive || isPaused) && phase !== 'complete' && (
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 max-w-sm w-full text-center">
              <p className="text-white/70 text-sm">
                {isPaused ? 'En pause' : 'Exercice de variation progressive du ratio inspiration/expiration. Durée : ~14 min'}
              </p>
            </div>
          )}

          {phase === 'complete' && (
            <div className="bg-emerald-500/20 backdrop-blur rounded-xl p-4 max-w-sm w-full text-center">
              <p className="text-emerald-300 font-bold">Exercice terminé ! 🎉</p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/30 backdrop-blur-md border-t border-white/10">
          <div className="flex items-center justify-center gap-4 px-4 py-5">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-3 rounded-full transition-colors ${soundEnabled ? 'bg-white/20 text-white' : 'bg-white/10 text-white/50'}`}
            >
              {soundEnabled ? <Volume2 size={22} /> : <VolumeX size={22} />}
            </button>

            {!isActive || phase === 'complete' ? (
              <button
                onClick={handleStart}
                className="p-5 bg-gradient-to-r from-cyan-500 to-violet-500 rounded-full shadow-lg hover:scale-105 transition-transform"
              >
                <Play size={28} className="text-white ml-1" />
              </button>
            ) : (
              <button
                onClick={handlePause}
                className={`p-5 rounded-full shadow-lg transition-all ${isPaused ? 'bg-gradient-to-r from-cyan-500 to-violet-500' : 'bg-white/20'}`}
              >
                {isPaused ? <Play size={28} className="text-white ml-1" /> : <Pause size={28} className="text-white" />}
              </button>
            )}

            <button onClick={handleReset} className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors">
              <RotateCcw size={22} className="text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RussianMastersModal;
